import { PipelineStepCategory } from '../../types';
import { PipelineStepParamsBuilder } from '../pipeline-step-params-builder';

/**
 * Structured view of a persisted step's params — the inverse projection of
 * `PipelineStepParamsBuilder.build()`. Captures the data the structured form
 * of the pipeline step editor needs to hydrate itself.
 */
export interface PipelineStepStructuredView {
  categories: PipelineStepCategory[];
  multiple: boolean;
  allowNewOptions: boolean;
  enableMosaicView: boolean;
  counter: boolean;
}

interface CategorizationEntry {
  category: string;
  options: string[];
}

interface OptionMeta {
  thrdisp?: number;
}

/**
 * Domain-side projector that inverts `PipelineStepParamsBuilder.build()`.
 *
 * Pairing both directions in the domain layer keeps the structured-params
 * schema as a single source of truth: any change to `STRUCTURED_PARAMS_KEYS`
 * or to the shape produced by `build()` must be reflected here too.
 */
export class PipelineStepDraftProjector {
  /**
   * Project persisted `params` into the structured editor view.
   *
   * Returns `null` when the params carry top-level keys that the structured
   * form cannot represent (i.e. were authored via Manual Mode). Callers
   * should fall back to a manual draft in that case to avoid silently
   * dropping data.
   */
  public static toStructuredView(params: Record<string, unknown>): PipelineStepStructuredView | null {
    if (!PipelineStepParamsBuilder.isStructuredCompatible(params)) {
      return null;
    }

    const categorization: CategorizationEntry[] = Array.isArray(params.categorization)
      ? (params.categorization as CategorizationEntry[])
      : [];
    const optionsMeta: Record<string, OptionMeta> = (params.options as Record<string, OptionMeta>) ?? {};

    const categories: PipelineStepCategory[] = categorization.map(
      (entry) =>
        new PipelineStepCategory({
          uuid: entry.category,
          name: { en: entry.category },
          options: (entry.options ?? []).map((optUuid) => ({
            uuid: optUuid,
            name: { en: optUuid },
            thrdis: typeof optionsMeta[optUuid]?.thrdisp === 'number' ? (optionsMeta[optUuid].thrdisp as number) : 0,
          })),
        })
    );

    return {
      categories,
      multiple: !!params.multiple,
      allowNewOptions: !!params.allowNewOptions,
      enableMosaicView: !!params.enableMosaicView,
      counter: !!params.counter,
    };
  }
}
